/**
 * Trade Guard Hook — pre-submission validation for individual trader
 *
 * Combines order guards (duplicate, slippage, size, value) with exposure
 * limits (per-market, per-sport, total) and rate monitoring into a single
 * validation gate called before every order placement.
 *
 * Also provides a client-side trade audit log (localStorage) for daily
 * loss tracking and historical review.
 */

import { useRef, useCallback, useState } from 'react';
import {
  runAllGuards,
  type OrderFingerprint,
  type GuardConfig,
  type GuardResult,
} from '../lib/orderGuards';
import {
  checkExposure,
  computeExposure,
  extractSport,
  type PositionExposure,
  type ExposureLimitConfig,
  type ExposureCheckResult,
} from '../lib/risk/exposureLimits';
import type { Position } from '../types';
import type { KalshiOrder } from '../lib/kalshiApi';

// ── Trade Audit Log ──────────────────────────────────────────────────

const TRADE_LOG_KEY = 'g1xl-trade-log-v1';
const MAX_LOG_ENTRIES = 5000;

export interface TradeLogEntry {
  ts: string;
  action: 'place' | 'cancel' | 'decrease';
  ticker: string;
  side: 'yes' | 'no';
  count: number;
  priceCents: number;
  valueDollars: number;
  result: 'success' | 'blocked' | 'error';
  reason?: string;
}

function readTradeLog(): TradeLogEntry[] {
  try {
    const raw = localStorage.getItem(TRADE_LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function appendTradeLog(entry: TradeLogEntry): void {
  try {
    const log = readTradeLog();
    log.push(entry);
    // Keep only the most recent entries
    const trimmed = log.length > MAX_LOG_ENTRIES ? log.slice(-MAX_LOG_ENTRIES) : log;
    localStorage.setItem(TRADE_LOG_KEY, JSON.stringify(trimmed));
  } catch {
    // Non-critical — don't break trading if localStorage is full
  }
}

/** Get today's trade log entries (for daily loss limit) */
function getTodaysLog(): TradeLogEntry[] {
  const today = new Date().toISOString().slice(0, 10);
  return readTradeLog().filter((e) => e.ts.startsWith(today));
}

// ── Rate Monitor ─────────────────────────────────────────────────────

interface RateWindow {
  timestamps: number[];
  maxPerWindow: number;
  windowMs: number;
}

function createRateWindow(maxPerWindow: number, windowMs: number): RateWindow {
  return { timestamps: [], maxPerWindow, windowMs };
}

function checkRate(window: RateWindow, now: number): { allowed: boolean; count: number } {
  // Purge expired timestamps
  const cutoff = now - window.windowMs;
  window.timestamps = window.timestamps.filter((t) => t >= cutoff);
  const count = window.timestamps.length;
  return { allowed: count < window.maxPerWindow, count };
}

function recordRate(window: RateWindow, now: number): void {
  window.timestamps.push(now);
}

// ── Guard Configuration ──────────────────────────────────────────────

export interface TradeGuardConfig {
  /** Order guard config (duplicate window, slippage, size limits) */
  guardConfig?: GuardConfig;
  /** Exposure limit config (per-market, per-sport, total) */
  exposureLimits?: Partial<ExposureLimitConfig>;
  /** Max orders per 10 seconds (default 10) */
  maxOrdersPer10s?: number;
  /** Max orders per 60 seconds (default 50) */
  maxOrdersPer60s?: number;
  /** Daily loss limit in dollars (default: disabled / Infinity) */
  dailyLossLimitDollars?: number;
  /** Enable/disable guard (default true) */
  enabled?: boolean;
}

export type TradeGuardResult = { ok: true } | { ok: false; reason: string; code: string };

export interface TradeGuardReturn {
  /** Validate an order before submission. Returns ok:true or ok:false with reason. */
  validateOrder: (args: {
    ticker: string;
    side: 'yes' | 'no';
    priceCents: number;
    quantity: number;
    /** Expected price for slippage check (usually same as priceCents for limit orders) */
    expectedPriceCents?: number;
  }) => TradeGuardResult;

  /** Log a trade result to the audit log */
  logTrade: (entry: TradeLogEntry) => void;

  /** Get today's trade count and stats */
  todayStats: {
    orderCount: number;
    blockedCount: number;
    errorCount: number;
  };

  /** Rate monitor status */
  rateStatus: {
    orders10s: number;
    orders60s: number;
    isThrottled: boolean;
  };

  /** Whether guards are enabled */
  enabled: boolean;
}

/**
 * Build PositionExposure[] from tracker positions + open orders.
 * Positions have averagePrice in 0..1, orders have yes_price/no_price in cents.
 */
function buildExposureList(positions: Position[], openOrders: KalshiOrder[]): PositionExposure[] {
  const result: PositionExposure[] = [];

  for (const pos of positions) {
    if (pos.quantity === 0) continue;
    const priceCents = Math.round(pos.averagePrice * 100);
    result.push({
      ticker: pos.ticker,
      sport: extractSport(pos.ticker),
      side: pos.side,
      quantity: Math.abs(pos.quantity),
      priceCents,
      exposureDollars: computeExposure(Math.abs(pos.quantity), priceCents, pos.side),
    });
  }

  for (const order of openOrders) {
    if (order.remaining_count <= 0) continue;
    const priceCents = order.yes_price ?? order.no_price ?? 0;
    result.push({
      ticker: order.ticker,
      sport: extractSport(order.ticker),
      side: order.side,
      quantity: order.remaining_count,
      priceCents,
      exposureDollars: computeExposure(order.remaining_count, priceCents, order.side),
    });
  }

  return result;
}

export function useTradeGuard(
  config: TradeGuardConfig = {},
  positionsRef: React.MutableRefObject<Position[]>,
  ordersRef: React.MutableRefObject<KalshiOrder[]>
): TradeGuardReturn {
  const enabled = config.enabled !== false;

  // Recent order fingerprints for duplicate detection
  const recentOrdersRef = useRef<OrderFingerprint[]>([]);

  // Rate windows
  const rate10sRef = useRef(createRateWindow(config.maxOrdersPer10s ?? 10, 10_000));
  const rate60sRef = useRef(createRateWindow(config.maxOrdersPer60s ?? 50, 60_000));

  const [rateStatus, setRateStatus] = useState({ orders10s: 0, orders60s: 0, isThrottled: false });
  const [todayStats, setTodayStats] = useState({ orderCount: 0, blockedCount: 0, errorCount: 0 });

  // Refresh today's stats from the log
  const refreshTodayStats = useCallback(() => {
    const today = getTodaysLog();
    setTodayStats({
      orderCount: today.filter((e) => e.action === 'place').length,
      blockedCount: today.filter((e) => e.result === 'blocked').length,
      errorCount: today.filter((e) => e.result === 'error').length,
    });
  }, []);

  const logTrade = useCallback(
    (entry: TradeLogEntry) => {
      appendTradeLog(entry);
      refreshTodayStats();
    },
    [refreshTodayStats]
  );

  const validateOrder = useCallback(
    (args: {
      ticker: string;
      side: 'yes' | 'no';
      priceCents: number;
      quantity: number;
      expectedPriceCents?: number;
    }): TradeGuardResult => {
      if (!enabled) return { ok: true };

      const now = Date.now();

      // 1. Rate check (10s and 60s windows)
      const rate10s = checkRate(rate10sRef.current, now);
      const rate60s = checkRate(rate60sRef.current, now);
      const isThrottled = !rate10s.allowed || !rate60s.allowed;
      setRateStatus({
        orders10s: rate10s.count,
        orders60s: rate60s.count,
        isThrottled,
      });

      if (!rate10s.allowed) {
        return {
          ok: false,
          reason: `Rate limit: ${rate10s.count} orders in 10s (max ${rate10sRef.current.maxPerWindow})`,
          code: 'rate_10s',
        };
      }
      if (!rate60s.allowed) {
        return {
          ok: false,
          reason: `Rate limit: ${rate60s.count} orders in 60s (max ${rate60sRef.current.maxPerWindow})`,
          code: 'rate_60s',
        };
      }

      // 2. Order guards (duplicate, slippage, size, value)
      const fingerprint: OrderFingerprint = {
        ticker: args.ticker,
        side: args.side,
        priceCents: args.priceCents,
        quantity: args.quantity,
        timestamp: now,
      };

      const guardResult: GuardResult = runAllGuards(
        fingerprint,
        args.expectedPriceCents ?? args.priceCents,
        recentOrdersRef.current,
        config.guardConfig
      );

      if (!guardResult.ok) {
        return { ok: false, reason: guardResult.reason, code: guardResult.code };
      }

      // 3. Exposure limits check
      const currentExposures = buildExposureList(positionsRef.current, ordersRef.current);
      const exposureResult: ExposureCheckResult = checkExposure(
        args.ticker,
        args.side,
        args.quantity,
        args.priceCents,
        currentExposures,
        config.exposureLimits
      );

      if (!exposureResult.ok) {
        return { ok: false, reason: exposureResult.reason, code: exposureResult.code };
      }

      // 4. Daily loss limit (if configured)
      const dailyLimit = config.dailyLossLimitDollars;
      if (dailyLimit != null && isFinite(dailyLimit)) {
        const todayLog = getTodaysLog();
        const todayLoss = todayLog
          .filter((e) => e.result === 'success' && e.action === 'place')
          .reduce((sum, e) => sum + e.valueDollars, 0);
        const newOrderValue = computeExposure(args.quantity, args.priceCents, args.side);
        if (todayLoss + newOrderValue > dailyLimit) {
          return {
            ok: false,
            reason: `Daily exposure $${(todayLoss + newOrderValue).toFixed(0)} would exceed $${dailyLimit.toLocaleString()} limit`,
            code: 'daily_limit',
          };
        }
      }

      // All checks passed — record the fingerprint + rate
      recentOrdersRef.current.push(fingerprint);
      // Trim old fingerprints (keep last 60s)
      const cutoff = now - 60_000;
      recentOrdersRef.current = recentOrdersRef.current.filter((f) => f.timestamp >= cutoff);

      recordRate(rate10sRef.current, now);
      recordRate(rate60sRef.current, now);

      return { ok: true };
    },
    [
      enabled,
      config.guardConfig,
      config.exposureLimits,
      config.dailyLossLimitDollars,
      positionsRef,
      ordersRef,
    ]
  );

  return {
    validateOrder,
    logTrade,
    todayStats,
    rateStatus,
    enabled,
  };
}
